/**
 * Dynamic Route Configuration
 *
 * Generates Refine resources and React Router routes from module manifests.
 * Modules with navigation.hidden = true are excluded from both navigation and routes.
 */

import React from "react";
import { Route } from "react-router-dom";
import { Result, Button } from "antd";
import { useNavigate } from "react-router-dom";
import type { ResourceProps } from "@refinedev/core";
import { getAllModules } from "./registry";
import { getIconComponent } from "./icons";

/**
 * Placeholder component for modules without dedicated pages yet.
 * Shows a "coming soon" message with link back to dashboard.
 */
const PlaceholderPage: React.FC<{ moduleName: string }> = ({ moduleName }) => {
  const navigate = useNavigate();
  return (
    <Result
      status="info"
      title={`${moduleName} Module`}
      subTitle="This module is registered but pages are not yet implemented. Create pages in frontend/src/pages/ to enable full functionality."
      extra={
        <Button type="primary" onClick={() => navigate("/")}>
          Back to Dashboard
        </Button>
      }
    />
  );
};

// Lazy load page components to avoid circular dependencies
// and enable code splitting
const pageComponents: Record<
  string,
  {
    List: React.LazyExoticComponent<React.FC>;
    Show: React.LazyExoticComponent<React.FC>;
    Create: React.LazyExoticComponent<React.FC>;
    Edit: React.LazyExoticComponent<React.FC>;
  }
> = {
  accounts: {
    List: React.lazy(() =>
      import("@/pages/accounts").then((m) => ({ default: m.AccountList }))
    ),
    Show: React.lazy(() =>
      import("@/pages/accounts").then((m) => ({ default: m.AccountShow }))
    ),
    Create: React.lazy(() =>
      import("@/pages/accounts").then((m) => ({ default: m.AccountCreate }))
    ),
    Edit: React.lazy(() =>
      import("@/pages/accounts").then((m) => ({ default: m.AccountEdit }))
    ),
  },
  contacts: {
    List: React.lazy(() =>
      import("@/pages/contacts").then((m) => ({ default: m.ContactList }))
    ),
    Show: React.lazy(() =>
      import("@/pages/contacts").then((m) => ({ default: m.ContactShow }))
    ),
    Create: React.lazy(() =>
      import("@/pages/contacts").then((m) => ({ default: m.ContactCreate }))
    ),
    Edit: React.lazy(() =>
      import("@/pages/contacts").then((m) => ({ default: m.ContactEdit }))
    ),
  },
  opportunities: {
    List: React.lazy(() =>
      import("@/pages/opportunities").then((m) => ({
        default: m.OpportunityList,
      }))
    ),
    Show: React.lazy(() =>
      import("@/pages/opportunities").then((m) => ({
        default: m.OpportunityShow,
      }))
    ),
    Create: React.lazy(() =>
      import("@/pages/opportunities").then((m) => ({
        default: m.OpportunityCreate,
      }))
    ),
    Edit: React.lazy(() =>
      import("@/pages/opportunities").then((m) => ({
        default: m.OpportunityEdit,
      }))
    ),
  },
  events: {
    List: React.lazy(() =>
      import("@/pages/events").then((m) => ({ default: m.EventList }))
    ),
    Show: React.lazy(() =>
      import("@/pages/events").then((m) => ({ default: m.EventShow }))
    ),
    Create: React.lazy(() =>
      import("@/pages/events").then((m) => ({ default: m.EventCreate }))
    ),
    Edit: React.lazy(() =>
      import("@/pages/events").then((m) => ({ default: m.EventEdit }))
    ),
  },
  documents: {
    List: React.lazy(() =>
      import("@/pages/documents").then((m) => ({ default: m.DocumentList }))
    ),
    Show: React.lazy(() =>
      import("@/pages/documents").then((m) => ({ default: m.DocumentShow }))
    ),
    Create: React.lazy(() =>
      import("@/pages/documents").then((m) => ({ default: m.DocumentCreate }))
    ),
    Edit: React.lazy(() =>
      import("@/pages/documents").then((m) => ({ default: m.DocumentEdit }))
    ),
  },
  "sales-orders": {
    List: React.lazy(() =>
      import("@/pages/sales-orders").then((m) => ({
        default: m.SalesOrderList,
      }))
    ),
    Show: React.lazy(() =>
      import("@/pages/sales-orders").then((m) => ({
        default: m.SalesOrderShow,
      }))
    ),
    Create: React.lazy(() =>
      import("@/pages/sales-orders").then((m) => ({
        default: m.SalesOrderCreate,
      }))
    ),
    Edit: React.lazy(() =>
      import("@/pages/sales-orders").then((m) => ({
        default: m.SalesOrderEdit,
      }))
    ),
  },
  products: {
    List: React.lazy(() =>
      import("@/pages/products").then((m) => ({ default: m.ProductList }))
    ),
    Show: React.lazy(() =>
      import("@/pages/products").then((m) => ({ default: m.ProductShow }))
    ),
    Create: React.lazy(() =>
      import("@/pages/products").then((m) => ({ default: m.ProductCreate }))
    ),
    Edit: React.lazy(() =>
      import("@/pages/products").then((m) => ({ default: m.ProductEdit }))
    ),
  },
  offers: {
    List: React.lazy(() =>
      import("@/pages/offers").then((m) => ({ default: m.OfferList }))
    ),
    Show: React.lazy(() =>
      import("@/pages/offers").then((m) => ({ default: m.OfferShow }))
    ),
    Create: React.lazy(() =>
      import("@/pages/offers").then((m) => ({ default: m.OfferCreate }))
    ),
    Edit: React.lazy(() =>
      import("@/pages/offers").then((m) => ({ default: m.OfferEdit }))
    ),
  },
  invoices: {
    List: React.lazy(() =>
      import("@/pages/invoices").then((m) => ({ default: m.InvoiceList }))
    ),
    Show: React.lazy(() =>
      import("@/pages/invoices").then((m) => ({ default: m.InvoiceShow }))
    ),
    Create: React.lazy(() =>
      import("@/pages/invoices").then((m) => ({ default: m.InvoiceCreate }))
    ),
    Edit: React.lazy(() =>
      import("@/pages/invoices").then((m) => ({ default: m.InvoiceEdit }))
    ),
  },
  // Detail modules (hidden from navigation, accessible via ⌘K)
  "sales-order-details": {
    List: React.lazy(() =>
      import("@/pages/sales-order-details").then((m) => ({
        default: m.SalesOrderDetailList,
      }))
    ),
    Show: React.lazy(() =>
      import("@/pages/sales-order-details").then((m) => ({
        default: m.SalesOrderDetailShow,
      }))
    ),
    Create: React.lazy(() =>
      import("@/pages/sales-order-details").then((m) => ({
        default: m.SalesOrderDetailCreate,
      }))
    ),
    Edit: React.lazy(() =>
      import("@/pages/sales-order-details").then((m) => ({
        default: m.SalesOrderDetailEdit,
      }))
    ),
  },
  "offer-details": {
    List: React.lazy(() =>
      import("@/pages/offer-details").then((m) => ({
        default: m.OfferDetailList,
      }))
    ),
    Show: React.lazy(() =>
      import("@/pages/offer-details").then((m) => ({
        default: m.OfferDetailShow,
      }))
    ),
    Create: React.lazy(() =>
      import("@/pages/offer-details").then((m) => ({
        default: m.OfferDetailCreate,
      }))
    ),
    Edit: React.lazy(() =>
      import("@/pages/offer-details").then((m) => ({
        default: m.OfferDetailEdit,
      }))
    ),
  },
  "invoice-details": {
    List: React.lazy(() =>
      import("@/pages/invoice-details").then((m) => ({
        default: m.InvoiceDetailList,
      }))
    ),
    Show: React.lazy(() =>
      import("@/pages/invoice-details").then((m) => ({
        default: m.InvoiceDetailShow,
      }))
    ),
    Create: React.lazy(() =>
      import("@/pages/invoice-details").then((m) => ({
        default: m.InvoiceDetailCreate,
      }))
    ),
    Edit: React.lazy(() =>
      import("@/pages/invoice-details").then((m) => ({
        default: m.InvoiceDetailEdit,
      }))
    ),
  },
  projects: {
    List: React.lazy(() =>
      import("@/pages/projects").then((m) => ({ default: m.ProjectList }))
    ),
    Show: React.lazy(() =>
      import("@/pages/projects").then((m) => ({ default: m.ProjectShow }))
    ),
    Create: React.lazy(() =>
      import("@/pages/projects").then((m) => ({ default: m.ProjectCreate }))
    ),
    Edit: React.lazy(() =>
      import("@/pages/projects").then((m) => ({ default: m.ProjectEdit }))
    ),
  },
  "project-tasks": {
    List: React.lazy(() =>
      import("@/pages/project-tasks").then((m) => ({
        default: m.ProjectTaskList,
      }))
    ),
    Show: React.lazy(() =>
      import("@/pages/project-tasks").then((m) => ({
        default: m.ProjectTaskShow,
      }))
    ),
    Create: React.lazy(() =>
      import("@/pages/project-tasks").then((m) => ({
        default: m.ProjectTaskCreate,
      }))
    ),
    Edit: React.lazy(() =>
      import("@/pages/project-tasks").then((m) => ({
        default: m.ProjectTaskEdit,
      }))
    ),
  },
};

/**
 * Get visible modules (not hidden) for navigation.
 */
export function getVisibleModules() {
  return getAllModules().filter((m) => !m.navigation.hidden);
}

/**
 * Generate Refine resource definitions from ALL modules (including hidden).
 * Hidden modules need resources for routing to work, just not in navigation.
 */
export function getRefineResources(): ResourceProps[] {
  const resources: ResourceProps[] = [
    {
      name: "dashboard",
      list: "/",
      meta: {
        label: "Dashboard",
        icon: getIconComponent("HomeOutlined"),
      },
    },
  ];

  // Include ALL modules for resources (hidden ones need routes too)
  for (const module of getAllModules()) {
    resources.push({
      name: module.name,
      list: `/${module.name}`,
      show: `/${module.name}/:id`,
      create: `/${module.name}/create`,
      edit: `/${module.name}/:id/edit`,
      meta: {
        label: module.labelPlural,
        icon: getIconComponent(module.navigation.icon),
        // Mark hidden modules so Refine doesn't show them in auto-generated nav
        hide: module.navigation.hidden,
      },
    });
  }

  return resources;
}

/**
 * Generate React Router Route elements for ALL modules (including hidden).
 * Hidden modules are accessible via direct URL or command palette.
 * Modules without dedicated page components get a placeholder page.
 */
export function getModuleRoutes(): React.ReactNode[] {
  const routes: React.ReactNode[] = [];

  // Include ALL modules for routes (hidden ones are accessible via ⌘K)
  for (const module of getAllModules()) {
    const components = pageComponents[module.name];

    // If no page components exist, render placeholder for all routes
    if (!components) {
      routes.push(
        <Route key={module.name} path={`/${module.name}`}>
          <Route
            index
            element={<PlaceholderPage moduleName={module.labelPlural} />}
          />
          <Route
            path="create"
            element={<PlaceholderPage moduleName={module.labelPlural} />}
          />
          <Route
            path=":id"
            element={<PlaceholderPage moduleName={module.labelPlural} />}
          />
          <Route
            path=":id/edit"
            element={<PlaceholderPage moduleName={module.labelPlural} />}
          />
        </Route>
      );
      continue;
    }

    const { List, Show, Create, Edit } = components;

    routes.push(
      <Route key={module.name} path={`/${module.name}`}>
        <Route
          index
          element={
            <React.Suspense fallback={<div>Loading...</div>}>
              <List />
            </React.Suspense>
          }
        />
        <Route
          path="create"
          element={
            <React.Suspense fallback={<div>Loading...</div>}>
              <Create />
            </React.Suspense>
          }
        />
        <Route
          path=":id/edit"
          element={
            <React.Suspense fallback={<div>Loading...</div>}>
              <Edit />
            </React.Suspense>
          }
        />
        <Route
          path=":id"
          element={
            <React.Suspense fallback={<div>Loading...</div>}>
              <Show />
            </React.Suspense>
          }
        />
      </Route>
    );
  }

  return routes;
}

/**
 * Get navigation items for the context bar.
 * Returns modules sorted by navigation order.
 */
export function getNavigationItems() {
  return getVisibleModules()
    .sort((a, b) => a.navigation.order - b.navigation.order)
    .map((module) => ({
      name: module.name,
      label: module.labelPlural,
      path: `/${module.name}`,
      icon: module.navigation.icon,
      group: module.navigation.group,
    }));
}
